import React, { useState, useEffect, useRef } from 'react';
import { Typography, Box } from '@mui/material';

const enableAds = process.env.REACT_APP_ENABLE_ADS === 'true';

const DynamicAdSense = ({
  adType = 'auto',
  fullWidth = true,
  onError = null,
  onLoad = null,
  width = '100%',
  height = '100%',
  minHeight = '240px',
  style = {}
}: {
  adType?: string;
  fullWidth?: boolean;
  onError?: (err: any) => void;
  onLoad?: () => void;
  width?: string | number;
  height?: string | number;
  minHeight?: string | number;
  style?: React.CSSProperties
}) => {
  const [adConfig, setAdConfig] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const adRef = useRef(null);
  const [adLoaded, setAdLoaded] = useState(false);



  useEffect(() => {
    if (enableAds) {
      fetchAdConfig();
    }
  }, [adType]);

  useEffect(() => {
    if (enableAds && adConfig && !adLoaded && !adConfig.testMode) {
      loadGoogleAdsScript();
    }
  }, [adConfig, adLoaded]);

  if (!enableAds) {
    return null;
  }

  const fetchAdConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(
        `/.netlify/functions/get-ad-config?type=${adType}`
      );

      if (!response.ok) {
        // Fallback for dev if function doesn't exist or fails
        if (process.env.NODE_ENV === 'development') {
          setAdConfig({
            clientId: 'ca-pub-test',
            slotId: '123456789',
            testMode: true
          });
          setLoading(false);
          return;
        }
        throw new Error(`Failed to fetch ad config: ${response.status}`);
      }

      const config = await response.json();

      if (!config.clientId || !config.slotId) {
        throw new Error('Invalid ad configuration received');
      }

      setAdConfig(config);
    } catch (err: any) {
      console.error('Error fetching ad config:', err);
      // Fallback for development if fetch fails
      if (process.env.NODE_ENV === 'development') {
        setAdConfig({
          clientId: 'ca-pub-test',
          slotId: '123456789',
          testMode: true
        });
        setLoading(false);
        return;
      }
      setError(err.message);
      if (onError) onError(err);
    } finally {
      setLoading(false);
    }
  };

  const loadGoogleAdsScript = () => {
    if (document.querySelector('script[src*="adsbygoogle.js"]')) {
      initializeAd();
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${adConfig.clientId}`;
    script.crossOrigin = 'anonymous';

    script.onload = () => {
      initializeAd();
    };

    script.onerror = () => {
      setError('Failed to load Google AdSense script');
    };

    document.head.appendChild(script);
  };

  const initializeAd = () => {
    try {
      if ((window as any).adsbygoogle && adRef.current) {
        ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push(
          {}
        );
        setAdLoaded(true);
        if (onLoad) onLoad();
      }
    } catch (err) {
      console.error('Error initializing ad:', err);
      setError('Failed to initialize advertisement');
    }
  };

  const retryLoadAd = () => {
    setAdLoaded(false);
    fetchAdConfig();
  };

  return (
    <Box
      className='ad-container'
      sx={{
        width: width,
        height: height,
        minHeight: minHeight,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        ...style
      }}
    >
      {loading ? (
        <Typography variant='caption' color='text.secondary'>
          Loading Ad...
        </Typography>
      ) : error ? (
        <Typography variant='caption' color='text.secondary' display="none">
          {/* Hide error from user, just collapse or show nothing */}
          Ad could not be loaded.
        </Typography>
      ) : !adConfig ? (
        <Typography variant='caption' color='text.secondary' display="none">
          Ad unavailable.
        </Typography>
      ) : adConfig.testMode ? (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            minHeight: minHeight,
            border: '2px dashed',
            borderColor: 'grey.300',
            display: 'flex',
            position: 'relative',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'grey.50',
            borderRadius: 1,
            p: 2
          }}
        >
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{
              textAlign: 'center',
              fontWeight: 'medium',
            }}
          >
            Ad Placeholder (Test Mode)
          </Typography>
        </Box>
      ) : (
        <div
          style={{
            width: '100%',
            display: 'block',
            minHeight: typeof minHeight === 'number' ? `${minHeight}px` : minHeight,
            position: 'relative',
          }}
        >
          <ins
            ref={adRef}
            className='adsbygoogle'
            style={{
              display: 'block',
              width: '100%',
              minHeight: typeof minHeight === 'number' ? `${minHeight}px` : minHeight,
            }}
            data-ad-client={adConfig.clientId}
            data-ad-slot={adConfig.slotId}
            // data-ad-format={adConfig.format || adType}
            // data-full-width-responsive={fullWidth.toString()}
            data-ad-test={adConfig.testMode ? 'on' : undefined}
          />
        </div>
      )}
    </Box>
  );
};

export default DynamicAdSense;
